import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { Movie } from '@/lib/movies';

interface DemoPlayerProps {
  movie: Movie;
}

function formatSeconds(total: number) {
  const safe = Math.max(0, Math.floor(total));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export default function DemoPlayer({ movie }: DemoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [theater, setTheater] = useState(false);
  const [hint, setHint] = useState('演示模式：不会加载真实视频');
  const barRef = useRef<HTMLDivElement>(null);

  const totalSeconds = movie.duration * 60;
  const currentSeconds = (totalSeconds * progress) / 100;

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setPlaying(false);
          return 100;
        }
        return Math.min(100, prev + 0.4);
      });
    }, 500);
    return () => window.clearInterval(timer);
  }, [playing]);

  useEffect(() => {
    document.body.style.overflow = theater ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [theater]);

  const togglePlay = () => {
    if (progress >= 100) {
      setProgress(0);
      setPlaying(true);
      setHint('已从头开始演示播放');
      return;
    }
    setPlaying((prev) => !prev);
  };

  const seek = (event: MouseEvent<HTMLDivElement>) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = (event.clientX - rect.left) / rect.width;
    setProgress(Math.min(100, Math.max(0, ratio * 100)));
    setHint(`已跳转到 ${formatSeconds(totalSeconds * ratio)}（演示）`);
  };

  return (
    <div
      className={
        theater
          ? 'fixed inset-0 z-[80] bg-background-50 flex flex-col justify-center px-3 md:px-8'
          : 'w-full'
      }
    >
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-200 border border-background-300/60">
        <img
          src={movie.banner ?? movie.cover}
          alt={`${movie.title} 播放封面`}
          title={`${movie.title} - 演示播放器`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/45 to-background-50/25" />

        <div className="absolute top-3 left-3 md:top-4 md:left-4 flex flex-wrap items-center gap-2">
          <span className="h-7 px-2.5 rounded-md bg-accent-500 text-background-50 text-[11px] tracking-widest font-label flex items-center">
            DEMO
          </span>
          <span className="h-7 px-2.5 rounded-md bg-background-50/70 backdrop-blur-sm text-[11px] text-foreground-700 flex items-center">
            高清 1080P · 演示画面
          </span>
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? '暂停演示播放' : '开始演示播放'}
            className={`relative w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all cursor-pointer ${
              playing
                ? 'bg-background-50/25 backdrop-blur-sm border border-foreground-800/40 opacity-0 hover:opacity-100'
                : 'bg-primary-500 text-background-50 dark:text-foreground-950'
            }`}
          >
            <i
              className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-3xl md:text-4xl w-9 h-9 flex items-center justify-center`}
            />
          </button>
        </div>

        <div className="absolute inset-x-0 bottom-0 px-3 md:px-5 pb-3 md:pb-4">
          <div
            ref={barRef}
            onClick={seek}
            role="slider"
            tabIndex={0}
            aria-label="播放进度"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') setProgress((prev) => Math.min(100, prev + 2));
              if (event.key === 'ArrowLeft') setProgress((prev) => Math.max(0, prev - 2));
            }}
            className="group relative h-1.5 w-full rounded-full bg-background-500/50 cursor-pointer"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary-500"
              style={{ width: `${progress}%` }}
            />
            <span
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary-400 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>

          <div className="mt-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={togglePlay}
                className="w-8 h-8 rounded-md text-foreground-900 hover:text-primary-400 flex items-center justify-center cursor-pointer"
                aria-label={playing ? '暂停' : '播放'}
              >
                <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setMuted((prev) => !prev);
                  setHint(muted ? '演示音量：已恢复' : '演示音量：已静音');
                }}
                className="w-8 h-8 rounded-md text-foreground-900 hover:text-primary-400 flex items-center justify-center cursor-pointer"
                aria-label={muted ? '取消静音' : '静音'}
              >
                <i className={`${muted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-lg`} />
              </button>
              <span className="ml-1 text-[11px] md:text-xs text-foreground-700 font-label tracking-wider">
                {formatSeconds(currentSeconds)} / {formatSeconds(totalSeconds)}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setHint('清晰度切换为演示，不提供真实片源')}
                className="h-8 px-2.5 rounded-md text-[11px] text-foreground-800 hover:text-primary-400 border border-foreground-800/30 flex items-center cursor-pointer"
              >
                1080P
              </button>
              <button
                type="button"
                onClick={() => setTheater((prev) => !prev)}
                className="w-8 h-8 rounded-md text-foreground-900 hover:text-primary-400 flex items-center justify-center cursor-pointer"
                aria-label={theater ? '退出全屏演示' : '全屏演示'}
              >
                <i className={`${theater ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-lg`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-background-300/50 bg-background-100 px-3.5 py-2.5">
        <p className="flex items-center gap-2 text-[11px] md:text-xs text-foreground-600">
          <i className="ri-information-line w-4 h-4 flex items-center justify-center text-primary-400" />
          播放区为前端交互演示，不加载真实视频文件
        </p>
        <p className="text-[11px] text-foreground-500">{hint}</p>
      </div>
    </div>
  );
}